import {
    ObvTileDataState,
    ObvTileDataStateTimeSeriesItem,
} from 'widgets/obvTile/store/stream/ObvTileStreamState';
import { timeUtils } from '@instinet-analytics/core-lib';
import { TimeSeriesChartDataItem } from 'utils/components/TimeSeriesChart/TimeSeriesChartData';
import { DateTime } from 'luxon';
import {
    applyShowDataFor,
    ShowDataFor,
} from 'utils/components/ShowDataFor/ShowDataFor';
import isNil from 'lodash/isNil';
import {
    mapFromElementRangeArray,
    TimeSeriesDisableRange,
} from 'utils/timeseries/TimeSeriesDisableRange';
import { v4 as uuid } from 'uuid';
import { BucketedDataItem } from 'utils/timeseries/bucket';

const { mapEpochToTime } = timeUtils;

export interface ObvTileData {
    breaksInTrading: TimeSeriesDisableRange[];
    timeSeries: ObvTileDataTimeSeriesItem[];
    startTime: number;
    endTime: number;
    id: string;
}

export interface ObvTileDataTimeSeriesItem
    extends TimeSeriesChartDataItem,
        BucketedDataItem {
    obv: number;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'pos-obv'?: number;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'neg-obv'?: number;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    'zero-obv'?: number;
}

export function buildObvTileData(
    bucketSize: number,
    streamData: ObvTileDataState | undefined,
    showDataFor?: ShowDataFor
): ObvTileData | undefined {
    if (!streamData?.timeSeries?.length) {
        return undefined;
    }

    const filteredTimeSeries = streamData.timeSeries.filter(
        (t) => !isNil(t.obv)
    );
    const sorted = filteredTimeSeries.sort((a, b) => (a._i > b._i ? +1 : -1));
    const truncatedTimeSeries = applyShowDataFor(sorted, showDataFor);

    let timeSeries = truncatedTimeSeries.map(mapTimeSeriesItem);
    timeSeries = fillGaps(timeSeries);

    const breaksInTrading = mapFromElementRangeArray(streamData.disableRange);

    return {
        id: uuid(),
        breaksInTrading,
        timeSeries,
        startTime: timeSeries[0].startTime,
        endTime: timeSeries[timeSeries.length - 1].endTime,
    };

    function mapTimeSeriesItem(
        item: ObvTileDataStateTimeSeriesItem
    ): ObvTileDataTimeSeriesItem {
        const startTime = mapEpochToTime(item._i);
        const time = DateTime.fromMillis(startTime).plus({
            minutes: bucketSize / 2,
        });
        const endTime = time.plus({
            minutes: bucketSize / 2,
        });

        return {
            ...item,
            startTime,
            time: time.toMillis(),
            endTime: endTime.toMillis(),
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'pos-obv': item.obv > 0 ? item.obv : undefined,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'neg-obv': item.obv < 0 ? item.obv : undefined,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            'zero-obv': item.obv === 0 ? item.obv : undefined,
            midTime: time.toMillis(),
            endTimeDisplay: endTime.toMillis(),
        };
    }

    function fillGaps(timeSeries: ObvTileDataTimeSeriesItem[]) {
        const ret: ObvTileDataTimeSeriesItem[] = [];
        let prev: ObvTileDataTimeSeriesItem | undefined;

        for (const curr of timeSeries) {
            if (prev) {
                if (
                    (curr['pos-obv'] !== undefined &&
                        prev['pos-obv'] === undefined) ||
                    (curr['neg-obv'] !== undefined &&
                        prev['neg-obv'] === undefined) ||
                    (curr.obv === 0 && prev.obv !== 0)
                ) {
                    const fillerTime = curr.time - (curr.time - prev.time) / 2;
                    ret.push({
                        time: fillerTime,
                        obv: 0,
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        'pos-obv':
                            curr['pos-obv'] !== undefined ||
                            prev['pos-obv'] !== undefined
                                ? 0
                                : undefined,
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        'neg-obv':
                            curr['neg-obv'] !== undefined ||
                            prev['neg-obv'] !== undefined
                                ? 0
                                : undefined,
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        'zero-obv':
                            curr['zero-obv'] !== undefined ||
                            prev['zero-obv'] !== undefined
                                ? 0
                                : undefined,
                        startTime: fillerTime,
                        endTime: fillerTime,
                        midTime: fillerTime,
                        endTimeDisplay: fillerTime,
                    });
                }
            }
            ret.push(curr);
            prev = curr;
        }
        return ret;
    }
}
